/**
 * Memory monitoring utility for tracking and optimizing memory usage
 * Helps detect memory leaks and high memory consumption
 */

import { logger } from './logger';

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface MemoryMetrics {
  timestamp: number;
  usedMemoryMB: number;
  totalMemoryMB: number;
  limitMemoryMB: number;
  usagePercentage: number;
}

class MemoryMonitor {
  private isEnabled: boolean = false;
  private monitoringInterval: NodeJS.Timeout | null = null;
  private memoryHistory: MemoryMetrics[] = [];
  private readonly MAX_HISTORY = 100;
  private readonly WARNING_THRESHOLD = 0.8; // 80% of heap limit
  private readonly CRITICAL_THRESHOLD = 0.9; // 90% of heap limit

  constructor() {
    this.isEnabled = this.isSupported();
  }

  /**
   * Check if memory monitoring is supported in current environment
   */
  private isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'performance' in window &&
      'memory' in (window.performance as any)
    );
  }

  /**
   * Get current memory usage information
   */
  private getMemoryInfo(): MemoryInfo | null {
    if (!this.isEnabled) return null;

    const memory = (window.performance as any).memory;
    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
    };
  }

  /**
   * Convert bytes to megabytes
   */
  private bytesToMB(bytes: number): number {
    return Math.round(bytes / 1024 / 1024 * 100) / 100;
  }

  /**
   * Get formatted memory metrics
   */
  getCurrentMetrics(): MemoryMetrics | null {
    const memoryInfo = this.getMemoryInfo();
    if (!memoryInfo) return null;

    const usedMemoryMB = this.bytesToMB(memoryInfo.usedJSHeapSize);
    const totalMemoryMB = this.bytesToMB(memoryInfo.totalJSHeapSize);
    const limitMemoryMB = this.bytesToMB(memoryInfo.jsHeapSizeLimit);
    const usagePercentage = (memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit) * 100;

    return {
      timestamp: Date.now(),
      usedMemoryMB,
      totalMemoryMB,
      limitMemoryMB,
      usagePercentage: Math.round(usagePercentage * 100) / 100,
    };
  }

  /**
   * Start monitoring memory usage
   */
  startMonitoring(intervalMs: number = 30000): void {
    if (!this.isEnabled) {
      logger.warn('Memory monitoring not supported in this environment');
      return;
    }

    if (this.monitoringInterval) {
      this.stopMonitoring();
    }

    this.monitoringInterval = setInterval(() => {
      this.checkMemory();
    }, intervalMs);

    logger.log('Memory monitoring started');
  }

  /**
   * Stop monitoring memory usage
   */
  stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
      logger.log('Memory monitoring stopped');
    }
  }

  /**
   * Check current memory usage and log warnings if necessary
   */
  private checkMemory(): void {
    const metrics = this.getCurrentMetrics();
    if (!metrics) return;

    // Add to history
    this.memoryHistory.push(metrics);
    if (this.memoryHistory.length > this.MAX_HISTORY) {
      this.memoryHistory.shift();
    }

    const usageRatio = metrics.usagePercentage / 100;

    // Log warnings based on thresholds
    if (usageRatio >= this.CRITICAL_THRESHOLD) {
      logger.error(`🚨 CRITICAL: Memory usage at ${metrics.usagePercentage}% (${metrics.usedMemoryMB}MB/${metrics.limitMemoryMB}MB)`);
      this.triggerGarbageCollection();
    } else if (usageRatio >= this.WARNING_THRESHOLD) {
      logger.warn(`⚠️ WARNING: Memory usage at ${metrics.usagePercentage}% (${metrics.usedMemoryMB}MB/${metrics.limitMemoryMB}MB)`);
    }

    // Log normal status in development
    if (process.env.NODE_ENV === 'development') {
      logger.log(`📊 Memory: ${metrics.usedMemoryMB}MB used (${metrics.usagePercentage}%)`);
    }
  }

  /**
   * Force garbage collection if available (development only)
   */
  private triggerGarbageCollection(): void {
    if (process.env.NODE_ENV === 'development' && (window as any).gc) {
      try {
        (window as any).gc();
        logger.log('🗑️ Garbage collection triggered');
      } catch (error) {
        logger.log('Unable to trigger garbage collection');
      }
    }
  }

  /**
   * Get memory usage statistics
   */
  getMemoryStats(): {
    current: MemoryMetrics | null;
    average: number;
    peak: number;
    historyLength: number;
  } {
    const current = this.getCurrentMetrics();
    
    if (this.memoryHistory.length === 0) {
      return {
        current,
        average: 0,
        peak: 0,
        historyLength: 0,
      };
    }

    const usages = this.memoryHistory.map(m => m.usagePercentage);
    const average = usages.reduce((a, b) => a + b, 0) / usages.length;
    const peak = Math.max(...usages);

    return {
      current,
      average: Math.round(average * 100) / 100,
      peak: Math.round(peak * 100) / 100,
      historyLength: this.memoryHistory.length,
    };
  }

  /**
   * Log memory statistics to console
   */
  logStats(): void {
    const stats = this.getMemoryStats();
    logger.table({
      'Current Usage': stats.current ? `${stats.current.usagePercentage}%` : 'N/A',
      'Current Memory': stats.current ? `${stats.current.usedMemoryMB}MB` : 'N/A',
      'Average Usage': `${stats.average}%`,
      'Peak Usage': `${stats.peak}%`,
      'History Length': stats.historyLength,
    });
  }

  /**
   * Clear memory history
   */
  clearHistory(): void {
    this.memoryHistory = [];
    logger.log('Memory history cleared');
  }
}

// Create singleton instance
const memoryMonitor = new MemoryMonitor();

// Auto-start monitoring in development
if (process.env.NODE_ENV === 'development') {
  memoryMonitor.startMonitoring();
}

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).memoryMonitor = memoryMonitor;
}

export default memoryMonitor;
export { MemoryMonitor };